import {
    buildCompressionFunction,
    canCompress
} from "@atlassian/exposure-events-compressor";
import {
    UI_EVENT_TYPE,
    TRACK_EVENT_TYPE,
    SCREEN_EVENT_TYPE,
    OPERATIONAL_EVENT_TYPE
} from "@atlassian/wac-analytics-bridge/src/server";
import {
    type UnknownEvent,
    type AnalyticsWebClient,
    type AnalyticsEvent,
    isAnalyticsEvent
} from "@atlassian/wac-analytics-web-client-types/src";
import { Cookie } from "@atlassian/wac-cookie";
import AnalyticsClient, {
    CompressionRule,
    envType,
    userType,
    originType
} from "@atlassiansox/analytics-web-client";

const getEnvironment = (): string => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
            return envType.PROD;
        case "staging":
            return envType.STAGING;
        case "development":
            return envType.DEV;
        default:
            return envType.LOCAL;
    }
};

const shouldUseStargate = (): boolean => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
        case "staging":
            return true;
        case "development":
        default:
            return false;
    }
};

const getAtlassianUserId = () => {
    const userId = Cookie.readCookie("__aid_user_id");
    if (userId) {
        return decodeURIComponent(userId);
    }
    return null;
};

// REMOVED: Jira specifics
export default class DefaultAnalyticsWebClient implements AnalyticsWebClient {
    // export default class DefaultAnalyticsWebClient {
    public analyticsClient!: AnalyticsClient;

    // The time it took to initialise the client
    public initTime!: number;

    // Number of times an event was triggered
    public eventCount!: number;

    // Duration we're blocking the UI thread with operations
    public blockingDuration!: number;

    public resetBlockingDuration() {
        this.eventCount = 0;
        this.blockingDuration = 0;
    }

    // CHANGED
    public init() {
        const start = performance.now();
        this.analyticsClient = new AnalyticsClient(
            {
                env: getEnvironment(),
                product: "marketing",
                version: "1.0.0",
                origin: originType.WEB,
                locale: "en-us"
            },
            {
                useStargate: shouldUseStargate(),
                delayQueueCompressors: [
                    new CompressionRule(
                        canCompress,
                        buildCompressionFunction(50)
                    )
                ]
            }
        );
        if (!this.analyticsClient) {
            return false;
        }

        const aaid = getAtlassianUserId();
        if (aaid) {
            this.analyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, aaid);
        }

        // Set init time
        this.initTime = performance.now() - start;

        return true;
    }

    public sendEvent(event: AnalyticsEvent | UnknownEvent) {
        if (isAnalyticsEvent(event)) {
            const { type, payload } = event;
            const start = performance.now();

            switch (type) {
                case UI_EVENT_TYPE:
                    this.analyticsClient.sendUIEvent(payload);
                    break;
                case TRACK_EVENT_TYPE:
                    this.analyticsClient.sendTrackEvent(payload);
                    break;
                case OPERATIONAL_EVENT_TYPE:
                    this.analyticsClient.sendOperationalEvent(payload);
                    break;
                case SCREEN_EVENT_TYPE:
                    this.analyticsClient.sendScreenEvent(payload);
                    break;
                default:
                    break;
            }

            // Increment the blocking duration
            this.blockingDuration += performance.now() - start;
            this.eventCount += 1;
        }
    }

    public getInstance() {
        return this.analyticsClient;
    }
}
